import React, { useMemo, useState, useEffect } from 'react';
import { Modal, Steps, Button, Space, Radio, Select, Descriptions, Alert, Typography, Divider, InputNumber, Switch } from 'antd';
import { useI18n } from '../i18n/i18n';
import { calculateProtectionSettings } from '../utils/protectionCalculations';
import { recommendSettings } from '../utils/autotune';
import SegmentsEditor from './SegmentsEditor';

const { Title, Text } = Typography;

/**
 * 自动整定向导（初版）
 * - 支持过电流/方向/零序的曲线族与 TMS 推荐
 * - 三步：选择目标 → 调整曲线族 → 预览与应用
 */
const AutoTuningWizard = ({ open, onClose, initialValues, onApply }) => {
  const { t } = useI18n();
  const [current, setCurrent] = useState(0);
  const [goal, setGoal] = useState('selective'); // fast/selective/sensitive
  const [overrideCurve, setOverrideCurve] = useState(null);
  const type = (initialValues?.protectionType) || 'overcurrent';
  const [overrideAngle, setOverrideAngle] = useState(null);
  const [segmentsLocal, setSegmentsLocal] = useState(null);
  // 新增：距离区段百分比与差动选项本地状态
  const [zonePct, setZonePct] = useState({ z1: null, z2: null, z3: null });
  const [secondHarmonic, setSecondHarmonic] = useState(null);
  const [ctSaturationHint, setCtSaturationHint] = useState(false);
  const [restraintSlopeOverride, setRestraintSlopeOverride] = useState(null);

  useEffect(() => {
    if (open) {
      setCurrent(0);
      setGoal('selective');
      setOverrideCurve(null);
      setOverrideAngle(null);
      setSegmentsLocal(null);
      setZonePct({ z1: null, z2: null, z3: null });
      setSecondHarmonic(null);
      setCtSaturationHint(false);
      setRestraintSlopeOverride(null);
    }
  }, [open]);

  const reco = useMemo(() => recommendSettings(initialValues || {}, { goal }), [initialValues, goal]);

  const curveOptions = [
    { value: 'Normal Inverse', label: 'Normal Inverse' },
    { value: 'Very Inverse', label: 'Very Inverse' },
    { value: 'Extremely Inverse', label: 'Extremely Inverse' },
    { value: 'Long-time Inverse', label: 'Long-time Inverse' }
  ];

  const suggestions = useMemo(() => {
    const base = reco?.suggestions || {};
    const next = { ...base };
    if (type === 'overcurrent' || type === 'directional' || type === 'zero_sequence') {
      next.curveType = overrideCurve || base.curveType;
    }
    if (type === 'distance') {
      if (overrideAngle != null) next.lineAngleDeg = overrideAngle;
      if (zonePct?.z1 != null) next.zone1Percent = zonePct.z1;
      if (zonePct?.z2 != null) next.zone2Percent = zonePct.z2;
      if (zonePct?.z3 != null) next.zone3Percent = zonePct.z3;
    }
    if (type === 'differential') {
      if (Array.isArray(segmentsLocal)) next.segments = segmentsLocal;
      next.ctSaturationHint = !!ctSaturationHint;
      if (secondHarmonic != null) next.secondHarmonicBlocking = secondHarmonic;
      if (restraintSlopeOverride != null) next.restraintSlope = restraintSlopeOverride;
    }
    return next;
  }, [reco, overrideCurve, overrideAngle, segmentsLocal, type, zonePct, secondHarmonic, ctSaturationHint, restraintSlopeOverride]);

  const previewResult = useMemo(() => {
    try {
      if (!initialValues) return null;
      const values = { ...(initialValues || {}) };
      // 合并所有建议字段到预览参数
      Object.entries(suggestions || {}).forEach(([k, v]) => { values[k] = v; });
      return calculateProtectionSettings(values);
    } catch (_) {
      return null;
    }
  }, [initialValues, suggestions]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={<span>自动整定向导</span>}
      footer={null}
      width={720}
    >
      <Steps
        style={{ marginBottom: 16 }}
        current={current}
        items={(type === 'overcurrent' || type === 'directional' || type === 'zero_sequence')
          ? [
              { title: '选择目标' },
              { title: '调整曲线族' },
              { title: '预览与应用' }
            ]
          : (type === 'distance'
            ? [
                { title: '线路参数与角度' },
                { title: '预览与应用' }
              ]
            : [
                { title: '特性推荐与编辑' },
                { title: '预览与应用' }
              ])
        }
      />

      {(current === 0) && (type === 'overcurrent' || type === 'directional' || type === 'zero_sequence') && (
        <div>
          <Title level={5}>请选择整定目标</Title>
          <Radio.Group value={goal} onChange={(e) => setGoal(e.target.value)}>
            <Space direction="vertical">
              <Radio value="fast">快速动作（目标≈0.35s）</Radio>
              <Radio value="selective">选择性优先（目标≈0.5s）</Radio>
              <Radio value="sensitive">灵敏度优先（目标≈0.6s）</Radio>
            </Space>
          </Radio.Group>
          <Divider />
          <Alert type="info" showIcon message="说明" description={(
            <div>
              <p>向导将依据当前参数（额定电流、CT 比、故障电流）推导故障倍数 M，并推荐曲线族与 TMS 以满足目标动作时间。</p>
              <p>可在下一步修改曲线族，向导将自动更新推荐的 TMS。</p>
            </div>
          )} />
        </div>
      )}

      {(current === 1) && (type === 'overcurrent' || type === 'directional' || type === 'zero_sequence') && (
        <div>
          <Title level={5}>选择或确认曲线族</Title>
          <Select
            style={{ width: 280 }}
            value={overrideCurve || suggestions.curveType}
            onChange={(v) => setOverrideCurve(v)}
            options={curveOptions}
          />
          <Divider />
          {Array.isArray(reco?.rationale) && reco.rationale.length > 0 && (
            <Alert type="success" showIcon message="推荐依据" description={<div>
              {reco.rationale.map((line, idx) => (<div key={idx}>{line}</div>))}
            </div>} />
          )}
          {!!(reco?.warnings && reco.warnings.length) && (
            <Alert style={{ marginTop: 12 }} type="warning" showIcon message="注意" description={<div>
              {reco.warnings.map((line, idx) => (<div key={idx}>{line}</div>))}
            </div>} />
          )}
        </div>
      )}

      {(current === ((type === 'overcurrent' || type === 'directional' || type === 'zero_sequence') ? 2 : (type === 'distance' ? 1 : 1))) && (
        <div>
          <Title level={5}>预览与应用</Title>
          <Descriptions bordered size="small" column={1} style={{ marginBottom: 12 }}>
            {(type === 'overcurrent' || type === 'directional' || type === 'zero_sequence') && (
              <>
                <Descriptions.Item label="推荐曲线族">{suggestions.curveType}</Descriptions.Item>
                <Descriptions.Item label="推荐时间倍数 TMS">{typeof suggestions.timeMultiplier === 'number' ? suggestions.timeMultiplier : '-'}</Descriptions.Item>
              </>
            )}
            {type === 'distance' && (
              <>
                <Descriptions.Item label="线路角 θ(°)">{typeof suggestions.lineAngleDeg === 'number' ? suggestions.lineAngleDeg : '-'}</Descriptions.Item>
                <Descriptions.Item label="线路阻抗 R+jX (Ω)">{`${(suggestions.lineR_ohm ?? initialValues?.lineR_ohm ?? 0)} + j${(suggestions.lineX_ohm ?? initialValues?.lineX_ohm ?? 0)}`}</Descriptions.Item>
              </>
            )}
            {type === 'differential' && (
              <>
                <Descriptions.Item label="分段条数">{Array.isArray(suggestions.segments) ? suggestions.segments.length : 0}</Descriptions.Item>
              </>
            )}
          </Descriptions>
          {previewResult ? (
            <Alert type="info" showIcon message="预览说明" description={(
              <div>
                <p>将应用推荐值后重新计算，以下为预览要点：</p>
                <ul style={{ marginLeft: 16 }}>
                  {(type === 'overcurrent' || type === 'directional' || type === 'zero_sequence') && (
                    <>
                      <li>坐标类型：{previewResult.axesType || '-'}</li>
                      <li>启动电流（二次）：{typeof previewResult.pickupCurrent === 'number' ? `${previewResult.pickupCurrent} A` : '-'}</li>
                      <li>时间倍数（TMS）：{typeof previewResult.timeMultiplier === 'number' ? previewResult.timeMultiplier : '-'}</li>
                      <li>曲线族：{previewResult.curveType || '-'}</li>
                    </>
                  )}
                  {type === 'distance' && (
                    <>
                      <li>坐标类型：{previewResult.axesType || '-'}</li>
                      <li>区段一 Z1：{typeof previewResult.zone1Setting === 'number' ? `${previewResult.zone1Setting.toFixed(3)} Ω` : '-'}</li>
                      <li>区段二 Z2：{typeof previewResult.zone2Setting === 'number' ? `${previewResult.zone2Setting.toFixed(3)} Ω` : '-'}</li>
                      <li>区段三 Z3：{typeof previewResult.zone3Setting === 'number' ? `${previewResult.zone3Setting.toFixed(3)} Ω` : '-'}</li>
                      <li>覆盖系数 Reach：{typeof previewResult.reach === 'number' ? previewResult.reach.toFixed(2) : '-'}</li>
                    </>
                  )}
                  {type === 'differential' && (
                    <>
                      <li>坐标类型：{previewResult.axesType || '-'}</li>
                      <li>速断门槛（二次）：{typeof previewResult.diffInstantaneousSecondary === 'number' ? `${previewResult.diffInstantaneousSecondary} A` : '-'}</li>
                      <li>差动定值（二次）：{typeof previewResult.differentialCurrentSetting === 'number' ? `${previewResult.differentialCurrentSetting} A` : '-'}</li>
                      <li>比率制动斜率 K：{typeof previewResult.restraintSlope === 'number' ? previewResult.restraintSlope : '-'}</li>
                      <li>二次谐波闭锁：{typeof previewResult.secondHarmonicBlocking === 'number' ? previewResult.secondHarmonicBlocking : '-'}</li>
                    </>
                  )}
                </ul>
              </div>
            )} />
          ) : (
            <Alert type="warning" showIcon message="无法生成预览" description="请检查参数是否完整或切换保护类型。" />
          )}
        </div>
      )}

      {(current === 0) && type === 'distance' && (
        <div>
          <Title level={5}>确认线路参数与角度</Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="当前线路阻抗 R(Ω)">{(reco?.suggestions?.lineR_ohm ?? initialValues?.lineR_ohm ?? 0)}</Descriptions.Item>
              <Descriptions.Item label="当前线路阻抗 X(Ω)">{(reco?.suggestions?.lineX_ohm ?? initialValues?.lineX_ohm ?? 0)}</Descriptions.Item>
            </Descriptions>
            <Space align="center">
              <Text type="secondary">线路角 θ(°)</Text>
              <InputNumber min={0} max={90} step={0.1} value={overrideAngle ?? reco?.suggestions?.lineAngleDeg} onChange={(v) => setOverrideAngle(v)} />
            </Space>
            <Divider />
            <Title level={5}>区段覆盖百分比</Title>
            <Space wrap>
              <Space>
                <Text type="secondary">Z1(%)</Text>
                <InputNumber min={50} max={300} step={1}
                  value={zonePct.z1 ?? (previewResult?.secondaryImpedance ? +(previewResult.zone1Setting / previewResult.secondaryImpedance * 100).toFixed(1) : 85)}
                  onChange={(v) => setZonePct(p => ({ ...p, z1: v }))}
                />
              </Space>
              <Space>
                <Text type="secondary">Z2(%)</Text>
                <InputNumber min={60} max={300} step={1}
                  value={zonePct.z2 ?? (previewResult?.secondaryImpedance ? +(previewResult.zone2Setting / previewResult.secondaryImpedance * 100).toFixed(1) : 120)}
                  onChange={(v) => setZonePct(p => ({ ...p, z2: v }))}
                />
              </Space>
              <Space>
                <Text type="secondary">Z3(%)</Text>
                <InputNumber min={80} max={300} step={1}
                  value={zonePct.z3 ?? (previewResult?.secondaryImpedance ? +(previewResult.zone3Setting / previewResult.secondaryImpedance * 100).toFixed(1) : 180)}
                  onChange={(v) => setZonePct(p => ({ ...p, z3: v }))}
                />
              </Space>
            </Space>
          </Space>
          <Divider />
          {Array.isArray(reco?.rationale) && reco.rationale.length > 0 && (
            <Alert type="success" showIcon message="推荐依据" description={<div>
              {reco.rationale.map((line, idx) => (<div key={idx}>{line}</div>))}
            </div>} />
          )}
          {!!(reco?.warnings && reco.warnings.length) && (
            <Alert style={{ marginTop: 12 }} type="warning" showIcon message="注意" description={<div>
              {reco.warnings.map((line, idx) => (<div key={idx}>{line}</div>))}
            </div>} />
          )}
        </div>
      )}

      {(current === 0) && type === 'differential' && (
        <div>
          <Title level={5}>特性推荐与编辑（Id–Ir）</Title>
          <SegmentsEditor value={segmentsLocal ?? reco?.suggestions?.segments} onChange={setSegmentsLocal} />
          <Divider />
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space align="center">
              <Text type="secondary">提示 CT 饱和</Text>
              <Switch checked={ctSaturationHint} onChange={setCtSaturationHint} />
            </Space>
            <Space align="center">
              <Text type="secondary">二次谐波闭锁系数</Text>
              <InputNumber min={0.05} max={0.35} step={0.01}
                value={secondHarmonic ?? (previewResult?.secondHarmonicBlocking ?? 0.18)}
                onChange={setSecondHarmonic}
              />
            </Space>
            <Space align="center">
              <Text type="secondary">比率制动斜率 K</Text>
              <InputNumber min={0.2} max={1.2} step={0.05}
                value={restraintSlopeOverride ?? (previewResult?.restraintSlope ?? 0.5)}
                onChange={setRestraintSlopeOverride}
              />
            </Space>
          </Space>
          <Divider />
          {Array.isArray(reco?.rationale) && reco.rationale.length > 0 && (
            <Alert type="success" showIcon message="推荐依据" description={<div>
              {reco.rationale.map((line, idx) => (<div key={idx}>{line}</div>))}
            </div>} />
          )}
          {!!(reco?.warnings && reco.warnings.length) && (
            <Alert style={{ marginTop: 12 }} type="warning" showIcon message="注意" description={<div>
              {reco.warnings.map((line, idx) => (<div key={idx}>{line}</div>))}
            </div>} />
          )}
        </div>
      )}

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Button disabled={current === 0} onClick={() => setCurrent((c) => Math.max(c - 1, 0))}>上一步</Button>
          <Button
            type="primary"
            onClick={() => setCurrent((c) => {
              const lastIdx = (type === 'overcurrent' || type === 'directional' || type === 'zero_sequence') ? 2 : 1;
              return Math.min(c + 1, lastIdx);
            })}
            disabled={(type === 'overcurrent' || type === 'directional' || type === 'zero_sequence') ? (current === 2) : (current === 1)}
          >下一步</Button>
        </Space>
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" onClick={() => {
            if (!onApply) return;
            const base = reco?.suggestions || {};
            const userAdjustments = {};
            if (type === 'overcurrent' || type === 'directional' || type === 'zero_sequence') {
              if (overrideCurve != null && overrideCurve !== base.curveType) userAdjustments.curveType = overrideCurve;
            }
            if (type === 'distance') {
              if (overrideAngle != null && overrideAngle !== base.lineAngleDeg) userAdjustments.lineAngleDeg = overrideAngle;
              if (zonePct?.z1 != null) userAdjustments.zone1Percent = zonePct.z1;
              if (zonePct?.z2 != null) userAdjustments.zone2Percent = zonePct.z2;
              if (zonePct?.z3 != null) userAdjustments.zone3Percent = zonePct.z3;
            }
            if (type === 'differential') {
              if (Array.isArray(segmentsLocal)) userAdjustments.segments = segmentsLocal;
              if (secondHarmonic != null) userAdjustments.secondHarmonicBlocking = secondHarmonic;
              if (restraintSlopeOverride != null) userAdjustments.restraintSlope = restraintSlopeOverride;
              if (ctSaturationHint) userAdjustments.ctSaturationHint = true;
            }
            onApply({
              suggestions,
              meta: {
                appliedFields: reco?.appliedFields || Object.keys(suggestions || {}),
                rationale: reco?.rationale || [],
                warnings: reco?.warnings || [],
                goal,
                type,
                userAdjustments
              }
            });
          }} disabled={!(suggestions && Object.keys(suggestions).length)}>
            应用建议
          </Button>
        </Space>
      </div>
    </Modal>
  );
};

export default AutoTuningWizard;